import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FundingFieldPreview } from "@/modules/drawn-funding/FundingField";
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  CheckCircle,
  CirclePlus,
  GripVertical,
  Info,
  ListOrdered,
  Settings,
  SquarePen,
  Trash,
  X,
} from "lucide-react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import type {
  FieldDecisionRulesConfig,
  Field,
  FieldScoringConfig,
  FieldType,
  Section,
} from "./ParametersForms";
import EditorIntro from "./EditorIntro";

type UpsertFieldPayload = {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  options?: unknown;
  nativeField?: string;
  step: number;
  parentId?: number;
};

type RootItem =
  | { id: number; kind: "field"; step: number; field: Field }
  | { id: number; kind: "section"; step: number; section: Section };

type ProjectTypeOption = {
  id: number;
  label: string;
};

type VisibilityMode = "all" | "show_only" | "hide_only";

const SPECIAL_FIELD_TYPES: FieldType[] = [
  "plantedSpecies",
  "protectedSpecies",
  "country",
  "projectType",
];

const isSpecialFieldType = (type: FieldType): boolean =>
  SPECIAL_FIELD_TYPES.includes(type);
const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const hasHtmlContent = (value: string): boolean => HTML_TAG_REGEX.test(value);

const CreateForm: FC<{
  data: Field[];
  sections: Section[];
  stepId: number;
  stepDbId?: number;
  stepDescription?: string | null;
  canEditStepDescription?: boolean;
  allowScoring?: boolean;
  hasMapFieldInForm?: boolean;
  hasFundingFieldInForm?: boolean;
  hiddenFieldKeys?: string[];
  busy?: boolean;
  readOnly?: boolean;
  inlineError?: string | null;
  isProtectedDescriptifStep?: boolean;
  isFinancingPreviewStep?: boolean;
  isIntroStep?: boolean;
  protectedFieldKeys?: string[];
  stepName?: string | null;
  projectTypeOptions?: ProjectTypeOption[];
  projectTypeOptionsLoading?: boolean;
  stepVisibilityMode?: VisibilityMode;
  stepVisibilityProjectTypeIds?: number[];
  onSaveStepDescription: (description: string) => Promise<void>;
  onUpdateStepVisibility: (
    mode: VisibilityMode,
    projectTypeIds: number[],
  ) => Promise<void>;
  onCreateField: (payload: UpsertFieldPayload) => Promise<void>;
  onUpdateField: (
    fieldId: number,
    payload: UpsertFieldPayload,
  ) => Promise<void>;
  onUpdateFieldVisibility: (
    field: Field,
    mode: VisibilityMode,
    projectTypeIds: number[],
  ) => Promise<void>;
  onDeleteField: (fieldId: number) => Promise<void>;
  onReorderItems: (parentId: number, itemIds: number[]) => Promise<boolean>;
  onCreateSection: (title: string, description: string) => Promise<void>;
  onUpdateSection: (
    sectionId: number,
    title: string,
    description: string,
  ) => Promise<void>;
  onDeleteSection: (sectionId: number) => Promise<void>;
}> = ({
  data,
  sections,
  stepId,
  stepDbId,
  stepDescription = null,
  canEditStepDescription = true,
  allowScoring = true,
  hasMapFieldInForm = false,
  hasFundingFieldInForm = false,
  hiddenFieldKeys = [],
  busy = false,
  readOnly = false,
  inlineError = null,
  isProtectedDescriptifStep = false,
  isFinancingPreviewStep = false,
  isIntroStep = false,
  protectedFieldKeys = [],
  projectTypeOptions = [],
  projectTypeOptionsLoading = false,
  stepVisibilityMode = "all",
  stepVisibilityProjectTypeIds = [],
  onSaveStepDescription,
  onUpdateStepVisibility,
  onCreateField,
  onUpdateField,
  onUpdateFieldVisibility,
  onDeleteField,
  onReorderItems,
  onCreateSection,
  onUpdateSection,
  onDeleteSection,
  stepName,
}) => {
  const canMutate = !readOnly;
  const [editFieldId, setEditFieldId] = useState<number | null>(null);
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [activeParentId, setActiveParentId] = useState<number | undefined>(
    stepDbId,
  );
  const [isRootReordering, setIsRootReordering] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderedRootItems, setOrderedRootItems] = useState<RootItem[]>([]);
  const [draggedRootId, setDraggedRootId] = useState<number | null>(null);
  const [dragOverRootId, setDragOverRootId] = useState<number | null>(null);
  const [activeSectionReorderId, setActiveSectionReorderId] = useState<
    number | null
  >(null);
  const [orderedSectionFields, setOrderedSectionFields] = useState<Field[]>([]);
  const [draggedSectionFieldId, setDraggedSectionFieldId] = useState<
    number | null
  >(null);
  const [dragOverSectionFieldId, setDragOverSectionFieldId] = useState<
    number | null
  >(null);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [sectionTitleDraft, setSectionTitleDraft] = useState("");
  const [sectionDescriptionDraft, setSectionDescriptionDraft] = useState("");
  const [editingStepDescription, setEditingStepDescription] = useState(false);
  const [stepDescriptionDraft, setStepDescriptionDraft] = useState(
    stepDescription ?? "",
  );
  const [savingIntroLocally, setSavingIntroLocally] = useState(false);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const dragYRef = useRef<number | null>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);
  const protectedSet = new Set(protectedFieldKeys);
  const hiddenSet = new Set(hiddenFieldKeys.map((key) => key.trim().toLowerCase()));
  const normalizeKey = (value?: string | null) =>
    (value ?? "").trim().toLowerCase();
  const isHiddenField = (field: Field) => {
    const nativeKey = normalizeKey(field.nativeField);
    const nameKey = normalizeKey(field.name);
    return Boolean(
      (nativeKey && hiddenSet.has(nativeKey)) ||
        (nameKey && hiddenSet.has(nameKey)),
    );
  };

  const unsectionedFields = useMemo(
    () => data.filter((field) => !field.sectionId),
    [data],
  );
  const rootItems = useMemo<RootItem[]>(
    () =>
      [
        ...unsectionedFields
          .filter(
            (field): field is Field & { id: number } =>
              typeof field.id === "number",
          )
          .map((field) => ({
            id: field.id,
            kind: "field" as const,
            step: field.step ?? 0,
            field,
          })),
        ...(sections ?? [])
          .filter(
            (section): section is Section & { id: number } =>
              typeof section.id === "number",
          )
          .map((section) => ({
            id: section.id,
            kind: "section" as const,
            step: section.step ?? 0,
            section,
          })),
      ].sort((a, b) => (a.step ?? 0) - (b.step ?? 0)),
    [sections, unsectionedFields],
  );

  useEffect(() => {
    setOrderedRootItems(rootItems);
  }, [rootItems]);

  useEffect(() => {
    setActiveParentId(stepDbId);
  }, [stepDbId]);

  useEffect(() => {
    setEditingStepDescription(false);
    setStepDescriptionDraft(stepDescription ?? "");
    setSavingIntroLocally(false);
  }, [stepDescription, stepId]);

  const handleSaveIntroduction = async () => {
    if (busy || savingIntroLocally || !canMutate) {
      return;
    }

    setSavingIntroLocally(true);
    const startedAt = Date.now();

    try {
      await onSaveStepDescription(stepDescriptionDraft);
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, 2000 - elapsed);
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
    } finally {
      setSavingIntroLocally(false);
    }
  };

  const isLockedField = (field: Field) => {
    if (!isProtectedDescriptifStep) {
      return false;
    }
    const nativeKey = normalizeKey(field.nativeField);
    return Boolean(nativeKey) && protectedSet.has(nativeKey);
  };
  const getFieldGridClassName = (
    field: Field,
    extraClassName = "",
  ): string =>
    [
      "rounded-md py-1",
      field.type === "funding" ? "2xl:col-span-2" : "",
      extraClassName,
    ]
      .filter(Boolean)
      .join(" ");
  const isStepVisibilityConfigAllowed = false;

  const handleDelete = async (field: Field) => {
    if (!field.id || busy || isLockedField(field) || !canMutate) {
      return;
    }

    await onDeleteField(field.id);
  };

  const stopAutoScroll = () => {
    dragYRef.current = null;
    if (autoScrollIntervalRef.current !== null) {
      window.clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const getScrollableContainer = (): HTMLElement | null => {
    let node: HTMLElement | null = gridRef.current;
    while (node) {
      const { overflowY } = window.getComputedStyle(node);
      const canScroll =
        (overflowY === "auto" || overflowY === "scroll") &&
        node.scrollHeight > node.clientHeight;
      if (canScroll) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  };

  const startAutoScroll = () => {
    if (autoScrollIntervalRef.current !== null) {
      return;
    }

    autoScrollIntervalRef.current = window.setInterval(() => {
      const pointerY = dragYRef.current;
      if (pointerY === null) {
        return;
      }

      const threshold = 90;
      const speed = 18;
      const scrollContainer = getScrollableContainer();

      if (scrollContainer) {
        const rect = scrollContainer.getBoundingClientRect();
        if (pointerY < rect.top + threshold) {
          scrollContainer.scrollBy({ top: -speed });
        } else if (pointerY > rect.bottom - threshold) {
          scrollContainer.scrollBy({ top: speed });
        }
        return;
      }

      if (pointerY < threshold) {
        window.scrollBy({ top: -speed });
      } else if (pointerY > window.innerHeight - threshold) {
        window.scrollBy({ top: speed });
      }
    }, 16);
  };

  useEffect(
    () => () => {
      stopAutoScroll();
    },
    [],
  );

  const reorderFields = <T extends { id?: number; step?: number }>(
    list: T[],
    fromId: number,
    toId: number,
  ) => {
    const fromIndex = list.findIndex((f) => f.id === fromId);
    const toIndex = list.findIndex((f) => f.id === toId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      return list;
    }

    const reordered = [...list];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    return reordered.map((field, index) => ({
      ...field,
      step: index + 1,
    }));
  };

  const handleDropRootItem = (targetId: number) => {
    if (
      !draggedRootId ||
      draggedRootId === targetId ||
      busy ||
      savingOrder ||
      !canMutate
    ) {
      setDraggedRootId(null);
      setDragOverRootId(null);
      return;
    }

    const nextItems = reorderFields(orderedRootItems, draggedRootId, targetId);
    setOrderedRootItems(nextItems);
    setDraggedRootId(null);
    setDragOverRootId(null);
    stopAutoScroll();
  };

  const hasPendingRootOrderChanges =
    orderedRootItems.length !== rootItems.length ||
    orderedRootItems.some((item, index) => item.id !== rootItems[index]?.id);

  const handleSaveRootOrder = async () => {
    if (
      !stepDbId ||
      !hasPendingRootOrderChanges ||
      savingOrder ||
      busy ||
      !canMutate
    ) {
      return;
    }

    setSavingOrder(true);
    const success = await onReorderItems(
      stepDbId,
      orderedRootItems.map((item) => item.id),
    );
    setSavingOrder(false);

    if (success) {
      stopAutoScroll();
      setIsRootReordering(false);
    }
  };

  const handleCancelRootOrder = () => {
    setOrderedRootItems(rootItems);
    setDraggedRootId(null);
    setDragOverRootId(null);
    stopAutoScroll();
    setIsRootReordering(false);
  };

  const getSectionFields = (sectionId?: number) => {
    const base = data
      .filter((field) => field.sectionId === sectionId)
      .sort((a, b) => (a.step ?? 0) - (b.step ?? 0));

    if (activeSectionReorderId && sectionId === activeSectionReorderId) {
      return orderedSectionFields;
    }

    return base.filter((field) => !isHiddenField(field));
  };

  const hasPendingSectionOrderChanges = useMemo(() => {
    if (!activeSectionReorderId) return false;
    const initial = data
      .filter((field) => field.sectionId === activeSectionReorderId)
      .sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
    return (
      orderedSectionFields.length !== initial.length ||
      orderedSectionFields.some(
        (field, index) => field.id !== initial[index]?.id,
      )
    );
  }, [activeSectionReorderId, data, orderedSectionFields]);

  const startSectionReorder = (sectionId?: number) => {
    if (!sectionId || !canMutate) return;
    setActiveSectionReorderId(sectionId);
    setOrderedSectionFields(
      data
        .filter((field) => field.sectionId === sectionId)
        .sort((a, b) => (a.step ?? 0) - (b.step ?? 0)),
    );
    setDraggedSectionFieldId(null);
    setDragOverSectionFieldId(null);
  };

  const handleDropSectionField = (targetId: number) => {
    if (
      !draggedSectionFieldId ||
      draggedSectionFieldId === targetId ||
      busy ||
      savingOrder ||
      !canMutate
    ) {
      setDraggedSectionFieldId(null);
      setDragOverSectionFieldId(null);
      return;
    }
    const next = reorderFields(
      orderedSectionFields,
      draggedSectionFieldId,
      targetId,
    );
    setOrderedSectionFields(next);
    setDraggedSectionFieldId(null);
    setDragOverSectionFieldId(null);
  };

  const cancelSectionReorder = () => {
    setActiveSectionReorderId(null);
    setOrderedSectionFields([]);
    setDraggedSectionFieldId(null);
    setDragOverSectionFieldId(null);
  };

  const saveSectionReorder = async () => {
    if (
      !activeSectionReorderId ||
      !hasPendingSectionOrderChanges ||
      busy ||
      savingOrder ||
      !canMutate
    ) {
      return;
    }
    setSavingOrder(true);
    const success = await onReorderItems(
      activeSectionReorderId,
      orderedSectionFields
        .filter(
          (field): field is Field & { id: number } =>
            typeof field.id === "number",
        )
        .map((field) => field.id),
    );
    setSavingOrder(false);
    if (success) {
      cancelSectionReorder();
    }
  };

  const openCreateSectionDialog = () => {
    if (!canMutate) return;
    setEditingSectionId(null);
    setSectionTitleDraft("");
    setSectionDescriptionDraft("");
    setSectionDialogOpen(true);
  };

  const openEditSectionDialog = (section: Section) => {
    if (!canMutate) return;
    setEditingSectionId(section.id ?? null);
    setSectionTitleDraft(section.label);
    setSectionDescriptionDraft(section.description ?? "");
    setSectionDialogOpen(true);
  };

  const handleSaveSection = async () => {
    const safeTitle = sectionTitleDraft.trim();
    if (!safeTitle || busy || !canMutate) {
      return;
    }

    if (editingSectionId) {
      await onUpdateSection(
        editingSectionId,
        safeTitle,
        sectionDescriptionDraft,
      );
    } else {
      await onCreateSection(safeTitle, sectionDescriptionDraft);
    }

    setSectionDialogOpen(false);
    setEditingSectionId(null);
    setSectionTitleDraft("");
    setSectionDescriptionDraft("");
  };

  const openCreateFieldForParent = (parentId?: number) => {
    if (!canMutate) return;
    setEditFieldId(null);
    setActiveParentId(parentId ?? stepDbId);
    setEditOpen(true);
  };

  const displayRootItems = orderedRootItems.filter(
    (item) => !(item.kind === "field" && isHiddenField(item.field)),
  );
  const hasLockedRootItems = displayRootItems.some(
    (item) => item.kind === "field" && isLockedField(item.field),
  );
  const lockedRootItems = displayRootItems.filter(
    (item) => item.kind === "field" && isLockedField(item.field),
  );
  const unlockedRootItems = displayRootItems.filter(
    (item) => !(item.kind === "field" && isLockedField(item.field)),
  );
  const unlockedRootFields = unlockedRootItems.filter(
    (item): item is Extract<RootItem, { kind: "field" }> =>
      item.kind === "field",
  );
  const unlockedSections = unlockedRootItems.filter(
    (item): item is Extract<RootItem, { kind: "section" }> =>
      item.kind === "section",
  );
  const rootItemsForReorderDisplay = isProtectedDescriptifStep
    ? displayRootItems.filter(
        (item) => !(item.kind === "field" && isLockedField(item.field)),
      )
    : displayRootItems;
  const rootFieldsForReorderDisplay = rootItemsForReorderDisplay.filter(
    (item): item is Extract<RootItem, { kind: "field" }> =>
      item.kind === "field",
  );
  const sectionsForReorderDisplay = rootItemsForReorderDisplay.filter(
    (item): item is Extract<RootItem, { kind: "section" }> =>
      item.kind === "section",
  );

  return (
    <div className="w-full py-2">
      <div className="mb-4 border-b border-slate-100 pb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Etape active
        </p>
        <p className="text-base font-semibold text-slate-900">{stepName}</p>
        {isStepVisibilityConfigAllowed && (
          <div className="mt-2">
            <VisibilitySettingsPopover
              disabled={busy || !canMutate}
              projectTypeOptions={projectTypeOptions}
              projectTypeOptionsLoading={projectTypeOptionsLoading}
              mode={stepVisibilityMode}
              projectTypeIds={stepVisibilityProjectTypeIds}
              onSave={async (mode, projectTypeIds) => {
                await onUpdateStepVisibility(mode, projectTypeIds);
              }}
            />
          </div>
        )}
        {!isIntroStep && editingStepDescription ? (
          <div className="mt-2 flex items-start gap-2">
            <textarea
              value={stepDescriptionDraft}
              onChange={(e) => setStepDescriptionDraft(e.target.value)}
              placeholder="Description de l'etape"
              rows={3}
              className="w-full resize-y rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700"
            />
            <button
              type="button"
              className="rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
              onClick={async () => {
                await onSaveStepDescription(stepDescriptionDraft);
                setEditingStepDescription(false);
              }}
              disabled={busy || !canMutate}
            >
              <CheckCircle className="h-4 w-4" />
            </button>
          </div>
        ) : !isIntroStep && stepDescription?.trim() ? (
          <div className="mt-1 flex items-start justify-between gap-2">
            {hasHtmlContent(stepDescription) ? (
              <div
                className="m-0 break-words text-sm text-slate-600 [&_a]:text-blue-600 [&_a]:underline [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1"
                dangerouslySetInnerHTML={{ __html: stepDescription }}
              />
            ) : (
              <p className="m-0 whitespace-pre-wrap break-words text-sm text-slate-600">
                {stepDescription}
              </p>
            )}
            {canEditStepDescription && (
              <button
                type="button"
                className="rounded p-1 text-slate-500 transition-colors hover:bg-blue-50 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  setEditingStepDescription(true);
                  setStepDescriptionDraft(stepDescription ?? "");
                }}
                disabled={busy || !canMutate}
              >
                <SquarePen className="h-4 w-4" />
              </button>
            )}
          </div>
        ) : !isIntroStep ? (
          <button
            type="button"
            className="mt-1 w-fit rounded px-0 py-0 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
            onClick={() => {
              setEditingStepDescription(true);
              setStepDescriptionDraft("");
            }}
            disabled={busy || !canEditStepDescription || !canMutate}
          >
            Ajouter une description
          </button>
        ) : null}
        {busy && (
          <p className="mt-1 text-xs font-medium text-slate-500">
            Synchronisation en cours...
          </p>
        )}
        {inlineError && (
          <p className="mt-1 text-xs font-medium text-red-600">{inlineError}</p>
        )}
      </div>
      {isFinancingPreviewStep ? (
        <FinancingPreview />
      ) : isIntroStep ? (
        <div className="w-full">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-2 text-sm font-medium text-slate-700">
              Introduction du formulaire
            </p>
            <EditorIntro
              value={stepDescriptionDraft}
              onChange={setStepDescriptionDraft}
              disabled={busy || !canMutate}
              enableDocumentLinkUpload
            />
            {canMutate && (
              <div className="mt-3">
                <Button
                  type="button"
                  onClick={() => void handleSaveIntroduction()}
                  disabled={busy || savingIntroLocally}
                >
                  {busy || savingIntroLocally
                    ? "Sauvegarde..."
                    : "Sauvegarder l'introduction"}
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {isRootReordering && (
            <div className="mb-4">
              <p className="text-xs text-slate-500">
                Glissez-deposez les champs puis cliquez sur Enregistrer l'ordre.
              </p>
            </div>
          )}
          {!isRootReordering && hasLockedRootItems && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Ces champs sont requis par Verdeo et ne peuvent pas être modifiés.
            </div>
          )}
          {isRootReordering ? (
            <div
              ref={gridRef}
              className="grid w-full grid-cols-1 gap-5 2xl:grid-cols-2"
              onDragOver={(e) => {
                if (!draggedRootId) {
                  return;
                }
                e.preventDefault();
                dragYRef.current = e.clientY;
              }}
            >
              {hasLockedRootItems && (
                <>
                  {lockedRootItems.map((item, itemIndex) => (
                    <div
                      key={`${item.kind}-${item.id}-${itemIndex}`}
                      className={getFieldGridClassName(item.field)}
                    >
                      {item.kind === "field" && (
                        <FieldInput
                          field={item.field}
                          step={stepId}
                          allowScoring={allowScoring}
                          busy={busy}
                          locked={true}
                          readOnly={!canMutate}
                          reordering={false}
                          showVisibilitySetting={false}
                          visibilityMode={item.field.visibilityMode ?? "all"}
                          visibilityProjectTypeIds={
                            item.field.visibilityProjectTypeIds ?? []
                          }
                          projectTypeOptions={projectTypeOptions}
                          projectTypeOptionsLoading={projectTypeOptionsLoading}
                          onUpdateFieldVisibility={onUpdateFieldVisibility}
                          onEdit={(editedField) => {
                            setEditFieldId(editedField.id ?? null);
                            setActiveParentId(editedField.parentId ?? stepDbId);
                            setEditOpen(true);
                          }}
                          handleDelete={handleDelete}
                        />
                      )}
                    </div>
                  ))}
                  {(rootFieldsForReorderDisplay.length > 0 ||
                    sectionsForReorderDisplay.length > 0) && (
                    <div className="2xl:col-span-2 my-2 border-t border-slate-200" />
                  )}
                </>
              )}
              {rootFieldsForReorderDisplay.map((item, itemIndex) => (
                <div
                  key={`${item.kind}-${item.id}-${itemIndex}`}
                  className={getFieldGridClassName(
                    item.field,
                    dragOverRootId === item.id ? "ring-2 ring-emerald-200" : "",
                  )}
                  draggable={
                    !busy &&
                    !savingOrder &&
                    !isLockedField(item.field) &&
                    canMutate
                  }
                  onDragStart={() => {
                    if (!canMutate) return;
                    if (isLockedField(item.field)) return;
                    setDraggedRootId(item.id);
                    startAutoScroll();
                  }}
                  onDragOver={(e) => {
                    if (!canMutate) return;
                    if (!draggedRootId || draggedRootId === item.id) return;
                    e.preventDefault();
                    dragYRef.current = e.clientY;
                    setDragOverRootId(item.id);
                  }}
                  onDrop={(e) => {
                    if (!canMutate) return;
                    e.preventDefault();
                    dragYRef.current = e.clientY;
                    handleDropRootItem(item.id);
                  }}
                  onDragEnd={() => {
                    setDraggedRootId(null);
                    setDragOverRootId(null);
                    stopAutoScroll();
                  }}
                >
                  <FieldInput
                    field={item.field}
                    step={stepId}
                    allowScoring={allowScoring}
                    busy={busy}
                    locked={isLockedField(item.field)}
                    readOnly={!canMutate}
                    reordering={isRootReordering}
                    showVisibilitySetting={false}
                    visibilityMode={item.field.visibilityMode ?? "all"}
                    visibilityProjectTypeIds={
                      item.field.visibilityProjectTypeIds ?? []
                    }
                    projectTypeOptions={projectTypeOptions}
                    projectTypeOptionsLoading={projectTypeOptionsLoading}
                    onUpdateFieldVisibility={onUpdateFieldVisibility}
                    onEdit={(editedField) => {
                      setEditFieldId(editedField.id ?? null);
                      setActiveParentId(editedField.parentId ?? stepDbId);
                      setEditOpen(true);
                    }}
                    handleDelete={handleDelete}
                  />
                </div>
              ))}
              {rootFieldsForReorderDisplay.length > 0 &&
                sectionsForReorderDisplay.length > 0 && (
                  <div className="2xl:col-span-2 my-2 border-t border-slate-200" />
                )}
              {sectionsForReorderDisplay.map((item, itemIndex) => (
                <div
                  key={`${item.kind}-${item.id}-${itemIndex}`}
                  className={`rounded-md 2xl:col-span-2 ${dragOverRootId === item.id ? "ring-2 ring-emerald-200" : ""}`}
                  draggable={!busy && !savingOrder && canMutate}
                  onDragStart={() => {
                    if (!canMutate) return;
                    setDraggedRootId(item.id);
                    startAutoScroll();
                  }}
                  onDragOver={(e) => {
                    if (!canMutate) return;
                    if (!draggedRootId || draggedRootId === item.id) return;
                    e.preventDefault();
                    dragYRef.current = e.clientY;
                    setDragOverRootId(item.id);
                  }}
                  onDrop={(e) => {
                    if (!canMutate) return;
                    e.preventDefault();
                    dragYRef.current = e.clientY;
                    handleDropRootItem(item.id);
                  }}
                  onDragEnd={() => {
                    setDraggedRootId(null);
                    setDragOverRootId(null);
                    stopAutoScroll();
                  }}
                >
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-slate-400" />
                      <h3 className="text-base font-semibold text-slate-900">
                        {item.section.label}
                      </h3>
                    </div>
                    {item.section.description && (
                      hasHtmlContent(item.section.description) ? (
                        <div
                          className="mt-1 break-words text-sm text-slate-600 [&_a]:text-blue-600 [&_a]:underline [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1"
                          dangerouslySetInnerHTML={{
                            __html: item.section.description,
                          }}
                        />
                      ) : (
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-600">
                          {item.section.description}
                        </p>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div ref={gridRef} className="w-full">
              {hasLockedRootItems && (
                <div className="grid w-full grid-cols-1 gap-5 2xl:grid-cols-2">
                  {lockedRootItems.map((item, itemIndex) => (
                    <div
                      key={`${item.kind}-${item.id}-${itemIndex}`}
                      className={getFieldGridClassName(item.field)}
                    >
                      {item.kind === "field" && (
                        <FieldInput
                          field={item.field}
                          step={stepId}
                          allowScoring={allowScoring}
                          busy={busy}
                          locked={isLockedField(item.field)}
                          readOnly={!canMutate}
                          reordering={false}
                          showVisibilitySetting={false}
                          visibilityMode={item.field.visibilityMode ?? "all"}
                          visibilityProjectTypeIds={
                            item.field.visibilityProjectTypeIds ?? []
                          }
                          projectTypeOptions={projectTypeOptions}
                          projectTypeOptionsLoading={projectTypeOptionsLoading}
                          onUpdateFieldVisibility={onUpdateFieldVisibility}
                          onEdit={(editedField) => {
                            setEditFieldId(editedField.id ?? null);
                            setActiveParentId(editedField.parentId ?? stepDbId);
                            setEditOpen(true);
                          }}
                          handleDelete={handleDelete}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {hasLockedRootItems && unlockedRootItems.length > 0 && (
                <div className="my-5 border-t border-slate-200" />
              )}
              {unlockedRootFields.length > 0 && (
                <div className="grid w-full grid-cols-1 gap-5 2xl:grid-cols-2">
                  {unlockedRootFields.map((item, itemIndex) => (
                    <div
                      key={`${item.kind}-${item.id}-${itemIndex}`}
                      className={getFieldGridClassName(item.field)}
                    >
                      <FieldInput
                        field={item.field}
                        step={stepId}
                        allowScoring={allowScoring}
                        busy={busy}
                        locked={isLockedField(item.field)}
                        readOnly={!canMutate}
                        reordering={false}
                        showVisibilitySetting={false}
                        visibilityMode={item.field.visibilityMode ?? "all"}
                        visibilityProjectTypeIds={
                          item.field.visibilityProjectTypeIds ?? []
                        }
                        projectTypeOptions={projectTypeOptions}
                        projectTypeOptionsLoading={projectTypeOptionsLoading}
                        onUpdateFieldVisibility={onUpdateFieldVisibility}
                        onEdit={(editedField) => {
                          setEditFieldId(editedField.id ?? null);
                          setActiveParentId(editedField.parentId ?? stepDbId);
                          setEditOpen(true);
                        }}
                        handleDelete={handleDelete}
                      />
                    </div>
                  ))}
                </div>
              )}
              {unlockedRootFields.length > 0 && unlockedSections.length > 0 && (
                <div className="my-5 border-t border-slate-200" />
              )}
              <div className="grid w-full grid-cols-1 gap-5 2xl:grid-cols-2">
                {unlockedSections.map((item, itemIndex) => (
                  <div
                    key={`${item.kind}-${item.id}-${itemIndex}`}
                    className="rounded-md py-1 2xl:col-span-2"
                  >
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">
                            {item.section.label}
                          </h3>
                          {item.section.description && (
                            hasHtmlContent(item.section.description) ? (
                              <div
                                className="mt-1 break-words text-sm text-slate-600 [&_a]:text-blue-600 [&_a]:underline [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1"
                                dangerouslySetInnerHTML={{
                                  __html: item.section.description,
                                }}
                              />
                            ) : (
                              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-600">
                                {item.section.description}
                              </p>
                            )
                          )}
                        </div>
                        {canMutate && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="rounded p-1 text-slate-500 transition-colors hover:bg-blue-50 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={
                                busy ||
                                isRootReordering ||
                                activeSectionReorderId === item.section.id ||
                                !item.section.id
                              }
                              onClick={() => {
                                openEditSectionDialog(item.section);
                              }}
                            >
                              <SquarePen className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="rounded p-1 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={
                                busy ||
                                isRootReordering ||
                                activeSectionReorderId === item.section.id ||
                                !item.section.id
                              }
                              onClick={() => {
                                if (!item.section.id) return;
                                void onDeleteSection(item.section.id);
                              }}
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {activeSectionReorderId === item.section.id && (
                        <p className="mb-2 text-xs text-slate-500">
                          Glissez-deposez les champs de la section puis
                          enregistrez.
                        </p>
                      )}
                      <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                        {getSectionFields(item.section.id).map(
                          (field, fieldIndex) => (
                            <div
                              key={`${field.id ?? field.name}-${fieldIndex}`}
                              className={getFieldGridClassName(
                                field,
                                dragOverSectionFieldId === field.id
                                  ? "ring-2 ring-emerald-200"
                                  : "",
                              )}
                              draggable={
                                Boolean(field.id) &&
                                activeSectionReorderId === item.section.id &&
                                !busy &&
                                !savingOrder &&
                                canMutate
                              }
                              onDragStart={() => {
                                if (!canMutate) return;
                                if (
                                  !field.id ||
                                  activeSectionReorderId !== item.section.id
                                )
                                  return;
                                setDraggedSectionFieldId(field.id);
                              }}
                              onDragOver={(e) => {
                                if (!canMutate) return;
                                if (
                                  !field.id ||
                                  !draggedSectionFieldId ||
                                  draggedSectionFieldId === field.id ||
                                  activeSectionReorderId !== item.section.id
                                ) {
                                  return;
                                }
                                e.preventDefault();
                                setDragOverSectionFieldId(field.id);
                              }}
                              onDrop={(e) => {
                                if (!canMutate) return;
                                e.preventDefault();
                                if (
                                  !field.id ||
                                  activeSectionReorderId !== item.section.id
                                )
                                  return;
                                handleDropSectionField(field.id);
                              }}
                              onDragEnd={() => {
                                setDraggedSectionFieldId(null);
                                setDragOverSectionFieldId(null);
                              }}
                            >
                              <FieldInput
                                field={field}
                                step={stepId}
                                allowScoring={allowScoring}
                                busy={busy}
                                locked={isLockedField(field)}
                                readOnly={!canMutate}
                                reordering={
                                  activeSectionReorderId === item.section.id
                                }
                                showVisibilitySetting={false}
                                visibilityMode={field.visibilityMode ?? "all"}
                                visibilityProjectTypeIds={
                                  field.visibilityProjectTypeIds ?? []
                                }
                                projectTypeOptions={projectTypeOptions}
                                projectTypeOptionsLoading={
                                  projectTypeOptionsLoading
                                }
                                onUpdateFieldVisibility={onUpdateFieldVisibility}
                                onEdit={(editedField) => {
                                  setEditFieldId(editedField.id ?? null);
                                  setActiveParentId(
                                    editedField.parentId ?? item.section.id,
                                  );
                                  setEditOpen(true);
                                }}
                                handleDelete={handleDelete}
                              />
                            </div>
                          ),
                        )}
                      </div>
                      {canMutate && (
                        <div className="mt-3 flex gap-2">
                          {activeSectionReorderId !== item.section.id ? (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={
                                  busy ||
                                  savingOrder ||
                                  isRootReordering ||
                                  !item.section.id
                                }
                                onClick={() =>
                                  openCreateFieldForParent(item.section.id)
                                }
                              >
                                <CirclePlus className="mr-2 h-4 w-4" />
                                Ajouter un champ
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={
                                  busy ||
                                  savingOrder ||
                                  isRootReordering ||
                                  !item.section.id ||
                                  getSectionFields(item.section.id).length < 2
                                }
                                onClick={() =>
                                  startSectionReorder(item.section.id)
                                }
                              >
                                <ListOrdered className="mr-2 h-4 w-4" />
                                Reorganiser
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={busy || savingOrder}
                                onClick={cancelSectionReorder}
                              >
                                Annuler
                              </Button>
                              <Button
                                type="button"
                                disabled={
                                  busy ||
                                  savingOrder ||
                                  !hasPendingSectionOrderChanges
                                }
                                onClick={() => void saveSectionReorder()}
                              >
                                {savingOrder
                                  ? "Enregistrement..."
                                  : "Enregistrer"}
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <hr />
          <div className="mt-4 flex w-full flex-row gap-3">
            {!isRootReordering && canMutate && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsRootReordering(true)}
                disabled={
                  busy ||
                  savingOrder ||
                  rootItems.length < 2 ||
                  activeSectionReorderId !== null
                }
              >
                <ListOrdered className="mr-2 h-4 w-4" />
                Modifier l'ordre dans l'étape
              </Button>
            )}
            {!isRootReordering && canMutate && (
              <Button
                type="button"
                variant="outline"
                disabled={
                  busy ||
                  savingOrder ||
                  !stepDbId ||
                  activeSectionReorderId !== null
                }
                onClick={() => openCreateFieldForParent(stepDbId)}
              >
                <CirclePlus className="mr-2 h-4 w-4" />
                Ajouter une Question
              </Button>
            )}
            <AddField
              fields={data}
              fieldId={editFieldId}
              editOpen={editOpen}
              setEditOpen={setEditOpen}
              setEditFieldId={setEditFieldId}
              rootParentId={stepDbId}
              parentId={activeParentId}
              setParentId={setActiveParentId}
              hasMapFieldInForm={hasMapFieldInForm}
              hasFundingFieldInForm={hasFundingFieldInForm}
              allowScoring={allowScoring}
              busy={
                busy ||
                isRootReordering ||
                savingOrder ||
                activeSectionReorderId !== null
              }
              canWrite={Boolean(stepDbId) && canMutate}
              onCreateField={onCreateField}
              onUpdateField={onUpdateField}
            />
            {!isRootReordering && canMutate && (
              <Button
                type="button"
                variant="outline"
                disabled={
                  busy ||
                  savingOrder ||
                  !stepDbId ||
                  activeSectionReorderId !== null
                }
                onClick={openCreateSectionDialog}
              >
                <CirclePlus className="mr-2 h-4 w-4" />
                Ajouter une section
              </Button>
            )}
            {isRootReordering && canMutate && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelRootOrder}
                  disabled={busy || savingOrder}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleSaveRootOrder()}
                  disabled={busy || savingOrder || !hasPendingRootOrderChanges}
                >
                  {savingOrder ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </>
            )}
          </div>
        </>
      )}
      <Dialog
        open={sectionDialogOpen}
        onOpenChange={(open) => {
          setSectionDialogOpen(open);
          if (!open) {
            setEditingSectionId(null);
            setSectionTitleDraft("");
            setSectionDescriptionDraft("");
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingSectionId ? "Modifier la section" : "Ajouter une section"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 p-2"
              placeholder="Titre de la section"
              value={sectionTitleDraft}
              onChange={(e) => setSectionTitleDraft(e.target.value)}
            />
            <textarea
              className="w-full resize-y rounded-md border border-gray-300 p-2"
              rows={4}
              placeholder="Description (optionnelle)"
              value={sectionDescriptionDraft}
              onChange={(e) => setSectionDescriptionDraft(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => void handleSaveSection()}
              disabled={busy}
            >
              {busy ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSectionDialogOpen(false)}
              disabled={busy}
            >
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FinancingPreview: FC<{ title?: string; showLockedNotice?: boolean }> = ({
  title = "Aperçu de l'étape Financement",
  showLockedNotice = true,
}) => {
  return (
    <div className="w-full">
      <p className="mb-2 text-sm text-slate-600">
        {title}
      </p>
      {showLockedNotice && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Cette étape est requise par Verdeo pour le suivi des financements et ne
          peut pas être modifiée.
        </div>
      )}
      <Tabs defaultValue="obtainedFunding" className="w-full">
        <TabsList className="bg-initial border-primary w-full items-end justify-start rounded-none border-b p-0">
          <TabsTrigger
            value="obtainedFunding"
            className="sm:text-md border-primary me-4 rounded-none ps-0 text-xs data-[state=active]:border-b-4"
          >
            Financements déjà obtenus
          </TabsTrigger>
          <TabsTrigger
            value="desiredFunding"
            className="sm:text-md border-primary rounded-none ps-0 text-xs data-[state=active]:border-b-4"
          >
            Financements souhaités
          </TabsTrigger>
        </TabsList>
        <TabsContent value="obtainedFunding" className="pt-5">
          <FundingFieldPreview mode="obtained" />
        </TabsContent>
        <TabsContent value="desiredFunding" className="pt-5">
          <FundingFieldPreview mode="desired" />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const AddField: FC<{
  setEditOpen: (open: boolean) => void;
  editOpen: boolean;
  setEditFieldId: (field: number | null) => void;
  fields: Field[];
  rootParentId?: number;
  parentId?: number;
  setParentId: (parentId?: number) => void;
  fieldId?: number | null;
  hasMapFieldInForm?: boolean;
  hasFundingFieldInForm?: boolean;
  allowScoring?: boolean;
  busy: boolean;
  canWrite: boolean;
  onCreateField: (payload: UpsertFieldPayload) => Promise<void>;
  onUpdateField: (
    fieldId: number,
    payload: UpsertFieldPayload,
  ) => Promise<void>;
}> = ({
  fields,
  rootParentId,
  parentId,
  setParentId,
  fieldId,
  hasMapFieldInForm = false,
  hasFundingFieldInForm = false,
  allowScoring = true,
  setEditOpen,
  editOpen,
  setEditFieldId,
  busy,
  canWrite,
  onCreateField,
  onUpdateField,
}) => {
  const [selected, setSelected] = useState<FieldType>("text");
  const [options, setOptions] = useState<string[]>([]);
  const [option, setOption] = useState<string>("");
  const [label, setLabel] = useState<string>("");
  const [required, setRequired] = useState<boolean>(false);
  const [scoringMode, setScoringMode] = useState<FieldScoringMode>("none");
  const [scoringEnabled, setScoringEnabled] = useState<boolean>(false);
  const [choiceScores, setChoiceScores] = useState<Record<string, number>>({});
  const [disqualifyingChoices, setDisqualifyingChoices] = useState<
    Record<string, boolean>
  >({});
  const [manualMaxPoints, setManualMaxPoints] = useState<string>("");
  const [nativeField, setNativeField] = useState<string | null>(null);
  const [action, setAction] = useState<"add" | "edit">("add");
  const editingField = useMemo(
    () => fields.find((field) => field.id === fieldId),
    [fieldId, fields],
  );
  const canUseMapType = !hasMapFieldInForm || editingField?.type === "Map";
  const canUseFundingType =
    !hasFundingFieldInForm || editingField?.type === "funding";

  const multiSelect = ["checkbox", "radio", "select"];
  const canUseAutoScoring =
    multiSelect.includes(selected) && !isSpecialFieldType(selected);
  const canUseManualScoring = true;
  const availableScoringModes = [
    ...(canUseManualScoring
      ? [
          {
            value: "manual" as FieldScoringMode,
            label: "Je note moi-même",
          },
        ]
      : []),
    ...(canUseAutoScoring
      ? [
          {
            value: "auto_choice" as FieldScoringMode,
            label: "Notation automatique",
          },
        ]
      : []),
  ];

  const resetForm = (nextParentId?: number) => {
    setAction("add");
    setSelected("text");
    setOptions([]);
    setOption("");
    setLabel("");
    setRequired(false);
    setScoringEnabled(false);
    setScoringMode("none");
    setChoiceScores({});
    setDisqualifyingChoices({});
    setManualMaxPoints("");
    setNativeField(null);
    setParentId(nextParentId ?? rootParentId);
  };

  useEffect(() => {
    if (!fieldId) {
      resetForm(parentId ?? rootParentId);
      return;
    }

    const fieldEdited = editingField;
    if (!fieldEdited) {
      resetForm();
      return;
    }

    setAction("edit");
    setSelected(fieldEdited.type);
    setLabel(fieldEdited.label);
    setOptions(
      isSpecialFieldType(fieldEdited.type) ? [] : (fieldEdited.options ?? []),
    );
    setRequired(fieldEdited.required);
    setScoringEnabled(Boolean(fieldEdited.scoring?.enabled));
    setScoringMode(fieldEdited.scoring?.mode ?? "none");
    setChoiceScores(fieldEdited.scoring?.pointsByChoice ?? {});
    setDisqualifyingChoices(
      Object.fromEntries(
        (fieldEdited.decisionRules?.disqualifyingChoices ?? []).map(
          (choice) => [choice, true],
        ),
      ),
    );
    setManualMaxPoints(
      fieldEdited.scoring?.maxPoints !== undefined &&
        fieldEdited.scoring?.maxPoints !== null
        ? String(fieldEdited.scoring.maxPoints)
        : "",
    );
    setNativeField(fieldEdited.nativeField ?? null);
    setParentId(fieldEdited.parentId ?? rootParentId);
  }, [editingField, fieldId, parentId, rootParentId, setParentId]);

  useEffect(() => {
    if (!canUseMapType && selected === "Map") {
      setSelected("text");
    }
    if (!canUseFundingType && selected === "funding") {
      setSelected("text");
    }
  }, [canUseFundingType, canUseMapType, selected]);

  useEffect(() => {
    if (isSpecialFieldType(selected)) {
      setOptions([]);
      setOption("");
    }
  }, [selected]);

  useEffect(() => {
    if (selected === "funding" && nativeField !== "desiredfunding") {
      setNativeField("desiredfunding");
      return;
    }

    if (selected !== "funding" && nativeField === "desiredfunding") {
      setNativeField(null);
    }
  }, [nativeField, selected]);

  useEffect(() => {
    if (!scoringEnabled && scoringMode !== "none") {
      setScoringMode("none");
      return;
    }

    if (!scoringEnabled) return;

    if (scoringMode === "auto_choice" && !canUseAutoScoring) {
      setScoringMode("none");
      return;
    }

    if (scoringMode === "manual" && !canUseManualScoring) {
      setScoringMode("none");
    }
  }, [canUseAutoScoring, canUseManualScoring, scoringEnabled, scoringMode]);

  useEffect(() => {
    if (!canUseAutoScoring) return;
    setChoiceScores((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (!options.includes(key)) {
          delete next[key];
        }
      });
      options.forEach((choice) => {
        if (next[choice] === undefined) {
          next[choice] = 0;
        }
      });
      return next;
    });
  }, [canUseAutoScoring, options]);

  useEffect(() => {
    if (!multiSelect.includes(selected) || isSpecialFieldType(selected)) {
      setDisqualifyingChoices({});
      return;
    }

    setDisqualifyingChoices((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (!options.includes(key)) {
          delete next[key];
        }
      });

      return next;
    });
  }, [options, selected]);

  const parsedManualMaxPoints = Number(manualMaxPoints);
  const isManualMaxValid =
    manualMaxPoints !== "" &&
    Number.isFinite(parsedManualMaxPoints) &&
    parsedManualMaxPoints >= 0;
  const requiresMinimumTwoOptions =
    selected === "checkbox" || selected === "radio";
  const hasMinimumChoiceOptions =
    !requiresMinimumTwoOptions || options.length >= 2;
  const hasValidChoiceConfiguration =
    !multiSelect.includes(selected) ||
    !canUseAutoScoring ||
    options.length > 0;
  const isScoringConfigValid = !allowScoring
    ? true
    : !scoringEnabled
    ? true
    : scoringMode === "auto_choice"
      ? canUseAutoScoring &&
        options.length > 0 &&
        hasMinimumChoiceOptions
      : scoringMode === "manual"
        ? canUseManualScoring && isManualMaxValid
        : false;
  const canSaveField =
    Boolean(label.trim()) &&
    !busy &&
    canWrite &&
    hasMinimumChoiceOptions &&
    hasValidChoiceConfiguration &&
    isScoringConfigValid;

  const buildScoringConfig = (): FieldScoringConfig | null => {
    if (!allowScoring) {
      return editingField?.scoring ?? null;
    }
    if (scoringMode === "none") return null;

    if (scoringMode === "auto_choice" && canUseAutoScoring) {
      const computedMaxPoints =
        selected === "checkbox"
          ? options.reduce(
              (total, choice) =>
                total + Math.max(0, Number(choiceScores[choice] ?? 0)),
              0,
            )
          : options.reduce(
              (max, choice) =>
                Math.max(max, Math.max(0, Number(choiceScores[choice] ?? 0))),
              0,
            );
      return {
        enabled: true,
        mode: "auto_choice",
        maxPoints: computedMaxPoints,
        pointsByChoice: Object.fromEntries(
          options.map((choice) => [
            choice,
            Math.max(0, Number(choiceScores[choice] ?? 0)),
          ]),
        ),
      };
    }

    if (scoringMode === "manual" && canUseManualScoring) {
      const parsed = Number(manualMaxPoints);
      return {
        enabled: true,
        mode: "manual",
        maxPoints: Number.isFinite(parsed) ? parsed : 0,
      };
    }

    return null;
  };

  const buildDecisionRulesConfig = (): FieldDecisionRulesConfig | null => {
    if (!multiSelect.includes(selected) || isSpecialFieldType(selected)) {
      return null;
    }

    const selectedChoices = options.filter(
      (choice) => disqualifyingChoices[choice],
    );

    if (selectedChoices.length === 0) {
      return null;
    }

    return {
      disqualifyingChoices: selectedChoices,
    };
  };

  const handleSave = async () => {
    const safeLabel = label.trim();
    if (
      !safeLabel ||
      busy ||
      !canWrite ||
      !hasMinimumChoiceOptions ||
      !hasValidChoiceConfiguration ||
      !isScoringConfigValid
    ) {
      return;
    }
    if (selected === "Map" && !canUseMapType) {
      return;
    }
    if (selected === "funding" && !canUseFundingType) {
      return;
    }

    const scoring = buildScoringConfig();
    const decisionRules = buildDecisionRulesConfig();
    const payloadOptions =
      multiSelect.includes(selected) && !isSpecialFieldType(selected)
        ? scoring || decisionRules
          ? {
              choices: options,
              ...(scoring ? { scoring } : {}),
              ...(decisionRules ? { decisionRules } : {}),
            }
          : options.length
            ? options
            : null
        : scoring || decisionRules
          ? {
              ...(scoring ? { scoring } : {}),
              ...(decisionRules ? { decisionRules } : {}),
            }
          : null;

    const payload: UpsertFieldPayload = {
      name: formatName(safeLabel),
      label: safeLabel,
      options: payloadOptions,
      type: selected,
      required,
      nativeField: selected === "funding" ? "desiredfunding" : nativeField ?? undefined,
      step: 1,
      parentId,
    };

    const siblings = fields.filter(
      (field) => field.parentId === (payload.parentId ?? rootParentId),
    );
    payload.step = siblings.length
      ? Math.max(...siblings.map((f) => f.step ?? 0)) + 1
      : 1;

    if (action === "add") {
      await onCreateField(payload);
    } else if (fieldId) {
      const existingField = fields.find((f) => f.id === fieldId);
      const existingParentId = existingField?.parentId ?? rootParentId;
      const nextParentId = payload.parentId ?? rootParentId;
      const parentChanged = existingParentId !== nextParentId;
      const fieldStep = parentChanged
        ? payload.step
        : (existingField?.step ?? payload.step);
      await onUpdateField(fieldId, {
        ...payload,
        step: fieldStep,
      });
    }

    setEditOpen(false);
    setEditFieldId(null);
    resetForm();
  };

  return (
    <Dialog
      open={editOpen}
      onOpenChange={(open) => {
        setEditOpen(open);
        if (!open) {
          setEditFieldId(null);
          resetForm(parentId ?? rootParentId);
        }
      }}
    >
      <DialogContent className="max-w-2xl px-14 py-8">
        <DialogHeader>
          <DialogTitle>Ajouter une question</DialogTitle>
        </DialogHeader>
        <div>
          <div className="flex flex-row gap-3">
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 p-2"
              placeholder="Intitulé du champ"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Select
              value={selected}
              onValueChange={(value: FieldType) => {
                setSelected(value);
                setScoringMode("none");
                setChoiceScores({});
                setDisqualifyingChoices({});
                setManualMaxPoints("");
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue
                  placeholder={
                    selected ? getLabelFromValue(selected) : "Type de champ"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="text">Réponse courte</SelectItem>
                  <SelectItem value="textarea">Paragraphe</SelectItem>
                  <SelectItem value="checkbox">Choix multiple</SelectItem>
                  <SelectItem value="radio">Case à cocher</SelectItem>
                  <SelectItem value="select">Liste déroulante</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="number">Nombre</SelectItem>
                  <SelectItem value="Map" disabled={!canUseMapType}>
                    Carte
                  </SelectItem>
                  <SelectItem value="upload">Fichiers</SelectItem>
                  <SelectItem value="funding" disabled={!canUseFundingType}>
                    Financement
                  </SelectItem>
                  <SelectItem value="plantedSpecies">
                    Essences plantées
                  </SelectItem>
                  <SelectItem value="protectedSpecies">
                    Espèces protégées
                  </SelectItem>
                  <SelectItem value="country">Pays</SelectItem>
                  <SelectItem value="projectType">
                    Choix du type de projet
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div>
            {multiSelect.includes(selected) &&
              !isSpecialFieldType(selected) && (
                <div>
                  <div className="mt-5 flex flex-row items-center gap-2">
                    <p className="m-0">
                      Ajouter des options pour le champ {selected} :
                    </p>
                    <input
                      type="text"
                      className="rounded-md border border-gray-300 p-2"
                      placeholder="Option"
                      value={option}
                      onChange={(e) => setOption(e.target.value)}
                    />
                    <Button
                      className="ml-2"
                      onClick={() => {
                        if (option.trim()) {
                          setOptions([...options, option.trim()]);
                          setOption("");
                        }
                      }}
                      type="button"
                    >
                      Ajouter
                    </Button>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-1">
                    {options.map((opt, index) => (
                      <div
                        key={index}
                        className="flex flex-row items-center gap-2 rounded-full bg-blue-300 px-5"
                      >
                        <p className="m-0">{opt}</p>
                        <X
                          className="cursor-pointer hover:text-red-500"
                          onClick={() =>
                            setOptions(options.filter((_, i) => i !== index))
                          }
                        />
                      </div>
                    ))}
                  </div>
                  {requiresMinimumTwoOptions && !hasMinimumChoiceOptions && (
                    <p className="mt-2 text-xs text-red-600">
                      Ce type de champ doit comporter au minimum deux options.
                    </p>
                  )}
                  {allowScoring && options.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <p className="m-0 text-sm font-medium text-slate-800">
                          Réponses rédhibitoires
                        </p>
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-500 transition hover:text-slate-700"
                                aria-label="Informations sur les réponses rédhibitoires"
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              Une réponse rédhibitoire entraîne un rejet automatique si le
                              système global de notation du formulaire est activé, même si ce
                              champ ne donne aucun point.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="space-y-2">
                        {options.map((choice) => (
                          <label
                            key={`disqualifying-${choice}`}
                            className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
                          >
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                                {choice}
                              </span>
                              {Boolean(disqualifyingChoices[choice]) && (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-red-600"
                                        aria-label={`L'option ${choice} est rédhibitoire`}
                                      >
                                        <AlertTriangle className="h-4 w-4" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="border-red-700 bg-red-600 text-xs text-white">
                                      Si le candidat coche cette option, sa candidature sera rejetée.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={Boolean(disqualifyingChoices[choice])}
                                onCheckedChange={(checked: boolean) =>
                                  setDisqualifyingChoices((prev) => ({
                                    ...prev,
                                    [choice]: checked,
                                  }))
                                }
                              />
                              <span className="text-xs font-medium text-slate-600">
                                Rédhibitoire
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-6">
            <div className="flex flex-row items-center gap-3">
              <Switch
                id="Obligatoire"
                checked={required}
                onCheckedChange={(e: boolean) => {
                  setRequired(e);
                }}
              />
              <label htmlFor="Obligatoire" className="m-0">
                Champ obligatoire
              </label>
            </div>
            {allowScoring && (canUseAutoScoring || canUseManualScoring) && (
              <div className="flex flex-row items-center gap-3">
                <Switch
                  id="scoring-enabled"
                  checked={scoringEnabled}
                  onCheckedChange={(value: boolean) => {
                    setScoringEnabled(value);
                    if (!value) {
                      setScoringMode("none");
                    }
                  }}
                />
                <label htmlFor="scoring-enabled" className="m-0">
                  Activer le système de notation
                </label>
              </div>
            )}
          </div>
          {allowScoring &&
            (canUseAutoScoring || canUseManualScoring) &&
            scoringEnabled && (
            <div className="mt-5 rounded-xl border border-[#dbe7d3] bg-[#f8fbf5] p-4">
              <div className="mb-4 space-y-1">
                <p className="m-0 text-sm font-semibold text-slate-800">
                  Système de notation activé
                </p>
                <p className="m-0 text-xs text-slate-600">
                  Seuls les champs notés sont pris en compte dans le score final. Un champ noté non renseigné compte comme 0, qu’il soit obligatoire ou non.
                </p>
              </div>

              <div className="mb-4 space-y-2">
                <label className="m-0 text-sm font-medium text-slate-800">
                  Mode de notation
                </label>
                <Select
                  key={`scoring-mode-${selected}-${scoringEnabled ? "enabled" : "disabled"}`}
                  value={scoringMode === "none" ? undefined : scoringMode}
                  onValueChange={(value: FieldScoringMode) =>
                    setScoringMode(value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sélectionner un mode de notation" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableScoringModes.map((mode) => (
                      <SelectItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {scoringMode === "none" && (
                  <p className="text-xs text-red-600">
                    Veuillez sélectionner un mode de notation pour cette question.
                  </p>
                )}
              </div>

              {scoringMode === "auto_choice" && canUseAutoScoring && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm text-slate-600">
                    Attribuez les points de chaque réponse. La note maximale sera
                    calculée automatiquement à partir des options configurées.
                    Si le champ reste vide, il comptera comme 0.
                    {selected === "checkbox"
                      ? " Pour un choix multiple, la note maximale correspond à la somme des notes de toutes les options."
                      : " Pour ce champ, la note maximale correspond à l'option ayant la note la plus élevée."}
                  </p>
                  {options.length === 0 ? (
                    <p className="text-sm text-amber-700">
                      Ajoutez d&apos;abord les choix du champ pour configurer la notation.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {options.map((choice) => (
                        <div
                          key={choice}
                          className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
                        >
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700">
                            {choice}
                          </span>
                          <input
                            type="number"
                            min={0}
                            className="w-28 rounded-md border border-gray-300 p-2 text-sm"
                            value={choiceScores[choice] ?? 0}
                            onChange={(e) =>
                              setChoiceScores((prev) => ({
                                ...prev,
                                [choice]: Math.max(0, Number(e.target.value || 0)),
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {scoringEnabled && scoringMode === "manual" && canUseManualScoring && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Ce champ sera évalué manuellement depuis le tableau de bord pour chaque projet candidat. S’il n’est pas renseigné, il comptera comme 0 dans le score final.
                  </p>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-slate-700">
                      Note maximale
                    </label>
                    <input
                      type="number"
                      min={0}
                      className="w-28 rounded-md border border-gray-300 p-2 text-sm"
                      value={manualMaxPoints}
                      onChange={(e) => setManualMaxPoints(e.target.value)}
                    />
                  </div>
                  {manualMaxPoints === "" && (
                    <p className="text-xs text-red-600">
                      Veuillez renseigner la note maximale de cette question.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={!canSaveField}
          >
            {busy ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
          <Button
            type="button"
            onClick={() => {
              resetForm();
              setEditFieldId(null);
              setEditOpen(false);
            }}
            disabled={busy}
          >
            Annuler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FieldInput: FC<{
  field: Field;
  step: number;
  allowScoring: boolean;
  busy: boolean;
  locked: boolean;
  readOnly: boolean;
  reordering: boolean;
  showVisibilitySetting: boolean;
  visibilityMode: VisibilityMode;
  visibilityProjectTypeIds: number[];
  projectTypeOptions: ProjectTypeOption[];
  projectTypeOptionsLoading: boolean;
  onUpdateFieldVisibility: (
    field: Field,
    mode: VisibilityMode,
    projectTypeIds: number[],
  ) => Promise<void>;
  onEdit: (field: Field) => void;
  handleDelete: (field: Field) => Promise<void>;
}> = ({
  field,
  step,
  allowScoring,
  busy,
  locked,
  readOnly,
  reordering,
  showVisibilitySetting,
  visibilityMode,
  visibilityProjectTypeIds,
  projectTypeOptions,
  projectTypeOptionsLoading,
  onUpdateFieldVisibility,
  onEdit,
  handleDelete,
}) => {
  const normalizedNativeField = (field.nativeField ?? "").trim().toLowerCase();
  const normalizedName = (field.name ?? "").trim().toLowerCase();
  const isPlantedSpecies =
    field.type === "plantedSpecies" ||
    normalizedNativeField === "plantedspecies" ||
    normalizedName === "plantedspecies";
  const isProtectedSpecies =
    field.type === "protectedSpecies" ||
    normalizedNativeField === "protectedspecies" ||
    normalizedName === "protectedspecies";
  const isCountry = field.type === "country";
  const isProjectType = field.type === "projectType";

  return (
    <div
      className={
        field.type === "funding"
          ? "flex w-full flex-col gap-3 py-1"
          : "flex w-full flex-row items-center gap-3 py-1"
      }
    >
      <div key={field.name} className={field.type === "funding" ? "w-full" : "w-2/3"}>
        <label
          htmlFor={`field-${step}-${field.name}`}
          className="block text-sm font-medium text-gray-700"
        >
          {field.label}{" "}
          {field.required && (
            <span className="text-destructive text-xs">*</span>
          )}
        </label>
        {field.scoring?.enabled && (
          <div className="mt-1 inline-flex rounded-full border border-[#cfe0c1] bg-[#eef6e7] px-2 py-0.5 text-[11px] font-medium text-[#355227]">
            {field.scoring.mode === "auto_choice"
              ? `Notation automatique${field.scoring.maxPoints !== null && field.scoring.maxPoints !== undefined ? ` / ${field.scoring.maxPoints}` : ""}`
              : `Notation manuelle${field.scoring.maxPoints !== null && field.scoring.maxPoints !== undefined ? ` / ${field.scoring.maxPoints}` : ""}`}
          </div>
        )}
        {allowScoring &&
          (field.decisionRules?.disqualifyingChoices?.length ?? 0) > 0 && (
          <div className="mt-1 ml-2 inline-flex rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
            Réponse rédhibitoire configurée
          </div>
        )}
        {isCountry ||
        isProjectType ||
        isPlantedSpecies ||
        isProtectedSpecies ? (
          <input
            type="text"
            id={`field-${step}-${field.name}`}
            name={`field-${step}-${field.name}`}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder={
              isPlantedSpecies || isProtectedSpecies
                ? "Commencez à taper"
                : "Sélectionner"
            }
            disabled
          />
        ) : field.type === "select" ? (
          <Select disabled>
            <SelectTrigger className="w-full pointer-events-none">
              <SelectValue placeholder="Sélectionnez une option" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {field.options?.map((option: string, index: number) => (
                  <SelectItem key={index} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        ) : field.type === "radio" || field.type === "checkbox" ? (
          <div className="mt-2 flex flex-wrap flex-row gap-5">
            {field.options?.map((option: string, index: number) => (
              <label key={index} className="mt-1 inline-flex items-center">
                <input
                  type={field.type}
                  name={`field-${step}-${field.name}`}
                  value={option}
                  className="form-radio text-blue-600"
                  disabled
                />
                <span className="ml-2">{option}</span>
              </label>
            ))}
          </div>
        ) : field.type === "textarea" ? (
          <textarea
            id={`field-${step}-${field.name}`}
            name={`field-${step}-${field.name}`}
            className="mt-1 block w-full resize-y rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            rows={4}
            disabled
          />
        ) : field.type === "funding" ? (
          <div className="mt-2">
            <FinancingPreview
              title="Aperçu du champ Financement"
              showLockedNotice={false}
            />
          </div>
        ) : field.type === "Map" ? (
          <div className="mt-1 rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500">
            Champ cartographique (apercu)
          </div>
        ) : (
          <input
            type={field.type}
            id={`field-${step}-${field.name}`}
            name={`field-${step}-${field.name}`}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            disabled
          />
        )}
      </div>
      <div className={field.type === "funding" ? "flex flex-row justify-end gap-2" : "mt-5 flex flex-row gap-2"}>
        {reordering && (
          <div className="rounded p-1 text-slate-400">
            <GripVertical className="h-4 w-4" />
          </div>
        )}
        {showVisibilitySetting && (
          <VisibilitySettingsPopover
            compact
            disabled={busy || reordering || readOnly || locked}
            projectTypeOptions={projectTypeOptions}
            projectTypeOptionsLoading={projectTypeOptionsLoading}
            mode={visibilityMode}
            projectTypeIds={visibilityProjectTypeIds}
            onSave={async (mode, projectTypeIds) => {
              if (!field.id) return;
              await onUpdateFieldVisibility(field, mode, projectTypeIds);
            }}
          />
        )}
        {!locked && (
          <button
            type="button"
            className="rounded p-1 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => void handleDelete(field)}
            disabled={busy || !field.id || reordering}
          >
            <Trash className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          className="rounded p-1 text-slate-500 transition-colors hover:bg-blue-50 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            if (!field.id) {
              return;
            }
            onEdit(field);
          }}
          disabled={busy || !field.id || reordering || locked}
        >
          <SquarePen className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const visibilityModeOptions: Array<{ value: VisibilityMode; label: string }> = [
  { value: "all", label: "Pour tous les types de projets" },
  { value: "show_only", label: "Afficher uniquement pour les types de projets" },
  { value: "hide_only", label: "Cacher uniquement pour les types de projets" },
];

const VisibilitySettingsPopover: FC<{
  mode: VisibilityMode;
  projectTypeIds: number[];
  projectTypeOptions: ProjectTypeOption[];
  projectTypeOptionsLoading?: boolean;
  disabled?: boolean;
  compact?: boolean;
  onSave: (mode: VisibilityMode, projectTypeIds: number[]) => Promise<void>;
}> = ({
  mode,
  projectTypeIds,
  projectTypeOptions,
  projectTypeOptionsLoading = false,
  disabled = false,
  compact = false,
  onSave,
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftMode, setDraftMode] = useState<VisibilityMode>(mode);
  const [draftProjectTypeIds, setDraftProjectTypeIds] = useState<number[]>(
    projectTypeIds,
  );

  useEffect(() => {
    if (!open) {
      setDraftMode(mode);
      setDraftProjectTypeIds(projectTypeIds);
    }
  }, [mode, open, projectTypeIds]);

  useEffect(() => {
    if (draftMode === "all" && draftProjectTypeIds.length > 0) {
      setDraftProjectTypeIds([]);
    }
  }, [draftMode, draftProjectTypeIds.length]);

  const selectedLabels = useMemo(() => {
    if (draftProjectTypeIds.length === 0) return "Selectionner des types";
    const labels = projectTypeOptions
      .filter((item) => draftProjectTypeIds.includes(item.id))
      .map((item) => item.label);
    return labels.length > 0 ? labels.join(", ") : "Selectionner des types";
  }, [draftProjectTypeIds, projectTypeOptions]);

  const toggleProjectType = (id: number) => {
    setDraftProjectTypeIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        className={`rounded p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50 ${compact ? "" : "inline-flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1 text-xs"}`}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <Settings className="h-4 w-4" />
        {!compact && <span>Parametres d'affichage</span>}
      </button>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Parametres d'affichage</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="mb-1 text-xs font-medium text-slate-600">Comportement</p>
            <Select
              value={draftMode}
              onValueChange={(value) => setDraftMode(value as VisibilityMode)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {visibilityModeOptions.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {draftMode !== "all" && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-600">
                Types de projets
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={projectTypeOptionsLoading || projectTypeOptions.length === 0}
                    className="h-10 w-full justify-between rounded-md border border-input bg-background px-3 py-2 text-sm font-normal"
                  >
                    <span className="truncate text-left">
                      {projectTypeOptionsLoading
                        ? "Chargement des types de projet..."
                        : projectTypeOptions.length === 0
                        ? "Aucun type de projet disponible"
                        : selectedLabels}
                    </span>
                    <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                  <Command>
                    <CommandInput placeholder="Rechercher un type de projet..." />
                    <CommandList>
                      <CommandEmpty>
                        {projectTypeOptionsLoading
                          ? "Chargement des types de projet..."
                          : "Aucun type de projet."}
                      </CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-auto">
                        {projectTypeOptions.map((option) => {
                          const selected = draftProjectTypeIds.includes(option.id);
                          return (
                            <CommandItem
                              key={option.id}
                              value={option.label}
                              onSelect={() => toggleProjectType(option.id)}
                              className="cursor-pointer"
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  selected ? "opacity-100" : "opacity-0"
                                }`}
                              />
                              <span className="truncate">{option.label}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {draftProjectTypeIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {projectTypeOptions
                    .filter((item) => draftProjectTypeIds.includes(item.id))
                    .map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700"
                        onClick={() => toggleProjectType(item.id)}
                      >
                        {item.label}
                        <X className="ml-1 h-3 w-3" />
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            type="button"
            disabled={
              saving ||
              (draftMode !== "all" && draftProjectTypeIds.length === 0)
            }
            onClick={async () => {
              setSaving(true);
              await onSave(
                draftMode,
                draftMode === "all" ? [] : draftProjectTypeIds,
              );
              setSaving(false);
              setOpen(false);
            }}
          >
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function formatName(name: string) {
  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
}

const fieldLabels: Record<string, string> = {
  text: "Reponse Courte",
  textarea: "Paragraphe",
  checkbox: "Choix multiple",
  radio: "Case a cocher",
  select: "Liste deroulante",
  date: "Date",
  number: "Nombre",
  funding: "Financement",
  Map: "Carte",
  upload: "Fichiers",
  plantedSpecies: "Essences plantées",
  protectedSpecies: "Espèces protégées",
  country: "Pays",
  projectType: "Choix types de projet",
};

function getLabelFromValue(value: string): string {
  return fieldLabels[value] ?? value;
}

export default CreateForm;
